<template>
  <div>
    <dropdown ref="dropDown" button-class="" :manual="true">
      <template slot="label">
        <double-click
          tag="span"
          class="drag-handle"
          @single-click="toggleDropdown"
          @double-click="configureColumn"
        >
          {{ translatedColumnName }} <span class="caret"></span>
        </double-click>
      </template>
      <li>
        <a @click.prevent="configureColumn">{{ labels.configureColumn }}</a>
      </li>
      <li>
        <a @click.prevent="addColumn({ position: 'left', from: column })">
          {{ labels.insertLeft }}
        </a>
      </li>
      <li>
        <a @click.prevent="addColumn({ position: 'right', from: column })">
          {{ labels.insertRight }}
        </a>
      </li>
      <li v-if="canDeleteColumns">
        <a @click.prevent="removeColumn(column)">{{ labels.deleteColumn }}</a>
      </li>
    </dropdown>
    <portal :target-el="`#${modalId}`">
      <modal
        v-if="showModal"
        v-model="showModal"
        :title="labels.column + ' > ' + columnName"
        :confirm-button-label="labels.save"
        :close-button-label="labels.cancel"
        @closed="showModal = false"
        @confirmed="save"
      >
        <translations
          :supported-languages="$attrs['supported-languages']"
          :label="labels.columnLabel"
          :translations="translations"
          :translated.sync="translated"
        >
        </translations>
        <div class="form-group">
          <label for="column-type">{{ labels.columnType }}</label>
          <select-field
            :items="inputTypes"
            :value="type"
            :empty-option="false"
            @selected="onSelect"
          >
          </select-field>
        </div>
        <div class="form-group">
          <div v-if="type === 'currency'">
            <select-field
              :items="currencies.list"
              :value="currency"
              :empty-option="false"
              aria-label="currency"
              @selected="onSelectCurrency"
            >
            </select-field>
          </div>
        </div>
      </modal>
    </portal>
  </div>
</template>

<script>
import { Dropdown, Modal, SelectField } from 'vue-bootstrap';
import { mapMutations } from 'vuex';
import DoubleClick from '../Shared/DoubleClick.vue';
import Translations from './TableFieldConfiguratorTranslations.vue';
import filtersMixin from './mixins/table-field-filters-mixin.js';
import translationsMixin from './mixins/table-field-translations-mixin.js';

export default {
  inject: ['currencies'],
  components: {
    Dropdown,
    Modal,
    SelectField,
    Translations,
    DoubleClick
  },
  mixins: [filtersMixin, translationsMixin],
  inheritAttrs: false,
  props: {
    columns: {
      type: Array,
      required: true
    },
    column: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    inputTypes: {
      type: Array,
      required: true
    },
    language: {
      type: String,
      required: true
    },
    labels: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showModal: false,
      type: null,
      currency: null,
      translated: {}
    };
  },
  computed: {
    translations() {
      return this.getTranslations(this.column);
    },
    translatedColumnName() {
      return this.getTranslation(this.language, this.column) || this.columnName;
    },
    columnName() {
      let prefix = '';
      let index = this.index;

      // Column index went over the available letters of the alphabet - add prefix
      if (index / 26 >= 1) {
        prefix = String.fromCharCode(65 + (Math.floor(index / 26) - 1));
        index = index % 26;
      }

      // Uppercase letters of the alphabet start at char code 65
      return (
        this.labels.column + ' ' + prefix + String.fromCharCode(65 + index)
      );
    },
    canDeleteColumns() {
      return this.columns.length > 1;
    },
    modalId() {
      return `modal-target-${this.column}`;
    }
  },
  created() {
    this.type = this.columnType(this.column);
    this.currency =
      this.columnSelectedCurrency(this.column) ||
      this.currencies.defaultCurrency;
  },
  beforeMount() {
    let modalTarget = document.getElementById(this.modalId);

    if (!modalTarget) {
      modalTarget = document.createElement('div');
      modalTarget.setAttribute('id', this.modalId);
      const appendTo =
        document.getElementById('pjaxContainer') || document.body;
      appendTo.appendChild(modalTarget);
    }
  },
  methods: {
    ...mapMutations(['insertColumn', 'deleteColumn']),
    toggleDropdown() {
      this.$refs.dropDown.toggle();
    },
    configureColumn() {
      this.showModal = true;
      this.$refs.dropDown.close();
    },
    addColumn(payload) {
      this.insertColumn(payload);
      this.$refs.dropDown.close();
    },
    save() {
      if (this.type) {
        this.saveFilters(
          this.column,
          this.type,
          this.currency ? { currency: this.currency } : {}
        );
      }

      this.saveTranslations(this.column, Object.assign({}, this.translated));
    },
    removeColumn(column) {
      // Make sure the clickOutside directive doesn't incorrectly detect
      // a click outside when the column disappears
      setTimeout(() => this.deleteColumn(column), 0);
    },
    onSelect(name, selected) {
      this.type = selected;
    },

    onSelectCurrency(name, selected) {
      this.currency = selected;
    }
  }
};
</script>
